import { Theme } from "@mui/material";
import { alpha, lighten } from "@mui/material/styles";
import { gainFill } from "../general/dataAtoms/shared";

/** What a revenue of the law firm consists of. */
export type RevenuePart =
  "indirectCosts" | "directCosts" | "cfaFees" | "margin";

/**
 * The order of the parts wherever they stand side by side or on top of one
 * another: the costs first, the profit margin last.
 */
export const REVENUE_PARTS: readonly RevenuePart[] = [
  "indirectCosts",
  "directCosts",
  "cfaFees",
  "margin",
];

/**
 * The two costs are tints of the primary colour, the darker one for the
 * indirect costs, as on the page of the fee structure.
 */
const INDIRECT_LIGHTEN = 0.2;
const DIRECT_LIGHTEN = 0.6;

/** How much of its colour the fill of the no-win fees takes. */
const NO_WIN_FEE_ALPHA = 0.45;

/** The fill of each part, and the colour of text on it. */
export function revenueColors(theme: Theme): {
  [part in RevenuePart | "cfaNoWinFee"]: { color: string; ink: string };
} {
  const indirect = lighten(theme.palette.primary.main, INDIRECT_LIGHTEN);
  const direct = lighten(theme.palette.primary.main, DIRECT_LIGHTEN);
  // a see-through fill takes the ink of the page
  const ink = theme.palette.text.primary;
  return {
    indirectCosts: {
      color: indirect,
      ink: theme.palette.getContrastText(indirect),
    },
    directCosts: { color: direct, ink: theme.palette.getContrastText(direct) },
    // the fees of a conditional fee agreement are a gain like the margin,
    // and stand back behind it
    cfaFees: { color: gainFill(theme, true), ink },
    // the fee of a case that is lost is told from the fee of one that is won
    cfaNoWinFee: {
      color: alpha(theme.palette.warning.main, NO_WIN_FEE_ALPHA),
      ink,
    },
    margin: { color: gainFill(theme), ink },
  };
}

/** Amounts within this of one another are the same amount. */
const SAME_AMOUNT = 0.01;

/**
 * The parts of a revenue as shares of it: each as a fraction, and as a whole
 * percentage such that the percentages add up to 100.
 *
 * Undefined where the parts are no shares of the revenue: where one of them
 * is negative, or they do not add up to it. That is so with a conditional fee
 * agreement, where the costs of the firm are what it puts in and are held as
 * negative amounts.
 */
export function revenueSplit(
  total: number,
  amounts: { [part in RevenuePart]: number },
): { part: RevenuePart; share: number; percent: number }[] | undefined {
  const parts = REVENUE_PARTS.map((part) => ({ part, amount: amounts[part] }));
  if (total <= 0) return undefined;
  if (parts.some(({ amount }) => amount < -SAME_AMOUNT)) return undefined;
  const sum = parts.reduce((all, { amount }) => all + amount, 0);
  if (Math.abs(sum - total) > SAME_AMOUNT) return undefined;

  const present = parts.filter(({ amount }) => amount > SAME_AMOUNT);
  // whole percentages by the largest remainder: each is rounded down, and
  // the points that are left go to the parts that lost the most by it
  const exact = present.map(({ amount }) => (amount / total) * 100);
  const percents = exact.map(Math.floor);
  let left = 100 - percents.reduce((all, percent) => all + percent, 0);
  exact
    .map((value, index) => ({ lost: value - percents[index], index }))
    .sort((a, b) => b.lost - a.lost)
    .forEach(({ index }) => {
      if (left > 0) {
        percents[index] += 1;
        left -= 1;
      }
    });

  return present.map(({ part, amount }, index) => ({
    part,
    share: amount / total,
    percent: percents[index],
  }));
}
